import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapError, getTransportError } from './apiUtils';

export type AccountLevel = 'group' | 'subgroup' | 'account' | 'subaccount';

export interface ChartAccount {
  id: string;
  code: string;
  name: string;
  level: AccountLevel | '';
  parentId: string;
}

export const mapChartAccount = (raw: any): ChartAccount => ({
  id:       raw.id != null ? String(raw.id) : '',
  code:     String(raw.code ?? ''),
  name:     raw.name || '',
  level:    raw.level || '',
  parentId: raw.parent_account_id != null ? String(raw.parent_account_id) : ''
});

export const listChartOfAccounts = async (): Promise<ChartAccount[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(
    `/chart_of_accounts_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`
  );
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map(mapChartAccount);
};

export const createChartOfAccount = async (payload: {
  code: string;
  name: string;
  level: AccountLevel;
  parentId?: string;
}): Promise<ChartAccount> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(
    `/chart_of_accounts_create${buildQuery({
      access_token:      token,
      company_id:        getActiveCompanyId(),
      code:              payload.code,
      name:              payload.name,
      level:             payload.level,
      parent_account_id: payload.parentId
    })}`
  );

  // El endpoint puede aceptar la llamada sin crear nada (response vacía): no es un éxito
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  const created = out ? out.response : null;
  if (created && typeof created === 'object') {
    return mapChartAccount({
      code: payload.code,
      name: payload.name,
      level: payload.level,
      parent_account_id: payload.parentId,
      ...created
    });
  }
  throw new Error(unwrapError(res, 'El servidor no confirmó la creación de la cuenta contable.'));
};

// Reglas del PGC: el nivel fija la longitud del código y cada cuenta cuelga del
// nivel inmediatamente superior, cuyo código es el comienzo del suyo (4 -> 43 -> 430 -> 4300001)
export const PARENT_LEVEL: Record<AccountLevel, AccountLevel | null> = {
  group: null,
  subgroup: 'group',
  account: 'subgroup',
  subaccount: 'account'
};

const PARENT_CODE_LENGTH: Record<AccountLevel, number> = {
  group: 0,
  subgroup: 1,
  account: 2,
  subaccount: 3
};

export const LEVEL_LABELS: Record<AccountLevel, string> = {
  group: 'Grupo',
  subgroup: 'Subgrupo',
  account: 'Cuenta',
  subaccount: 'Subcuenta'
};

const LENGTH_RULES: Record<AccountLevel, { ok: (code: string) => boolean; msg: string }> = {
  group: { ok: c => c.length === 1, msg: 'Un grupo tiene 1 dígito (1-9).' },
  subgroup: { ok: c => c.length === 2, msg: 'Un subgrupo tiene 2 dígitos (p. ej. 43).' },
  account: { ok: c => c.length === 3, msg: 'Una cuenta tiene 3 dígitos (p. ej. 430).' },
  subaccount: { ok: c => c.length >= 4, msg: 'Una subcuenta tiene 4 o más dígitos y amplía el código de su cuenta (p. ej. 4300001).' }
};

// Devuelve el error de validación, o la cuenta padre (si el nivel la necesita)
export const validateNewAccount = (
  code: string,
  level: AccountLevel,
  parentId: string,
  accounts: ChartAccount[]
): { error: string } | { parent: ChartAccount | null } => {
  if (!/^[0-9]+$/.test(code)) return { error: 'El código solo puede contener dígitos.' };
  if (!LENGTH_RULES[level].ok(code)) return { error: `Código no válido para el nivel elegido. ${LENGTH_RULES[level].msg}` };
  if (accounts.some(a => a.code === code)) return { error: `Ya existe una cuenta con el código ${code}.` };

  const parentLevel = PARENT_LEVEL[level];
  if (!parentLevel) return { parent: null };

  const parentLabel = LEVEL_LABELS[parentLevel].toLowerCase();
  const parentCode = code.slice(0, PARENT_CODE_LENGTH[level]);
  const chosen = parentId ? accounts.find(a => a.id === parentId) : undefined;
  const parent = chosen || accounts.find(a => a.code === parentCode && a.level === parentLevel);

  if (!parent || !parent.id) return { error: `Falta la cuenta padre ${parentCode} (${parentLabel}) en el plan: créala antes.` };
  if (parent.level && parent.level !== parentLevel) return { error: `La cuenta padre ${parent.code} no es válida: debe ser un ${parentLabel}.` };
  if (!code.startsWith(parent.code) || code === parent.code) return { error: `El código ${code} debe empezar por el de su cuenta padre (${parent.code}).` };
  return { parent };
};

export const PGC_GROUPS: Record<string, string> = {
  '1': 'Financiación básica',
  '2': 'Activo no corriente',
  '3': 'Existencias',
  '4': 'Acreedores y deudores',
  '5': 'Cuentas financieras',
  '6': 'Compras y gastos',
  '7': 'Ventas e ingresos',
  '8': 'Gastos imputados al PN',
  '9': 'Ingresos imputados al PN'
};
